import * as pdfjs from 'pdfjs-dist';

// Configure PDF.js worker - use unpkg CDN for the worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// CORS proxy function - use a public CORS proxy
const createProxiedUrl = (url) => {
  // Use a public CORS proxy
  return `https://corsproxy.io/?${encodeURIComponent(url)}`;
};

export const extractPDFArea = async (pdfUrl, x, y, width, height, scale = 2) => {
  try {
    // Load the PDF document through CORS proxy
    const proxiedUrl = createProxiedUrl(pdfUrl);
    console.log('Loading PDF through proxy:', proxiedUrl);
    const loadingTask = pdfjs.getDocument(proxiedUrl);
    const pdf = await loadingTask.promise;

    // Get the first page (for now, we'll assume single page or first page)
    const page = await pdf.getPage(1);

    // Get page dimensions
    const viewport = page.getViewport({ scale });

    // Create a canvas for rendering the full page at high resolution
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    // Render the PDF page to canvas
    const renderContext = {
      canvasContext: context,
      viewport: viewport
    };

    await page.render(renderContext).promise;

    // Create a new canvas for the extracted area
    const extractedCanvas = document.createElement('canvas');
    const extractedContext = extractedCanvas.getContext('2d');

    // Scale the selection coordinates to match the high-resolution rendering
    const scaledX = x * scale;
    const scaledY = y * scale;
    const scaledWidth = width * scale;
    const scaledHeight = height * scale;

    extractedCanvas.width = scaledWidth;
    extractedCanvas.height = scaledHeight;

    // Extract the selected area from the full page canvas
    extractedContext.drawImage(
      canvas,
      scaledX, scaledY, scaledWidth, scaledHeight, // Source rectangle
      0, 0, scaledWidth, scaledHeight // Destination rectangle
    );

    // Convert to maximum quality PNG (lossless)
    return extractedCanvas.toDataURL('image/png', 1.0);

  } catch (error) {
    console.error('Error extracting PDF area:', error);
    return null;
  }
};

export const getPDFPageCount = async (pdfUrl) => {
  try {
    let documentUrl;
    if (pdfUrl.startsWith('blob:') || pdfUrl.includes('localhost') || pdfUrl.includes('127.0.0.1')) {
      // Local file or localhost - use directly without proxy
      documentUrl = pdfUrl;
    } else {
      // Remote URL - use proxy
      documentUrl = createProxiedUrl(pdfUrl);
    }
    const loadingTask = pdfjs.getDocument(documentUrl);
    const pdf = await loadingTask.promise;
    return pdf.numPages;
  } catch (error) {
    console.error('Error getting PDF page count:', error);
    return 1;
  }
};

export const extractPDFAreaFromPage = async (pdfUrl, pageNumber, x, y, width, height, displayScale = 1, extractionScale = 2, rotation = 0, pdfData = null, loadedPdfDoc = null) => {
  try {
    let pdf;

    if (loadedPdfDoc) {
      try {
        console.log('Extracting PDF area using existing PDF document');
        // Test if we can access the document
        await loadedPdfDoc.getPage(1);
        pdf = loadedPdfDoc;
      } catch (e) {
        console.warn('Failed to use existing PDF doc for extraction, falling back to new load:', e);
        pdf = null;
      }
    }

    if (!pdf) {
      let loadingTask;
      if (pdfData) {
        console.log('Extracting PDF area from raw data (Uint8Array)');
        loadingTask = pdfjs.getDocument({ data: pdfData });
      } else {
        // Check if pdfUrl is a blob URL (local file), localhost, or a regular remote URL
        let documentUrl;
        if (pdfUrl.startsWith('blob:') || pdfUrl.includes('localhost') || pdfUrl.includes('127.0.0.1')) {
          // Local file or localhost - use directly without proxy
          documentUrl = pdfUrl;
          console.log('Extracting PDF area from local/localhost file:', { pdfUrl, pageNumber, x, y, width, height, displayScale, extractionScale, rotation });
        } else {
          // Remote URL - use proxy
          documentUrl = createProxiedUrl(pdfUrl);
          console.log('Extracting PDF area from remote URL through proxy:', { documentUrl, pageNumber, x, y, width, height, displayScale, extractionScale, rotation });
        }
        loadingTask = pdfjs.getDocument(documentUrl);
      }
      pdf = await loadingTask.promise;
    }

    // Get the specified page
    const page = await pdf.getPage(pageNumber);

    // Get page dimensions to determine if this is a large PDF (photo) or regular PDF (document)
    const pageViewport = page.getViewport({ scale: 1 });
    const pdfPageWidth = pageViewport.width;
    const pdfPageHeight = pageViewport.height;
    const maxPageDimension = Math.max(pdfPageWidth, pdfPageHeight);

    // Calculate the final extracted area size to check against canvas limits
    const scaleRatio = extractionScale / displayScale;
    const finalWidth = width * scaleRatio;
    const finalHeight = height * scaleRatio;

    // Browser canvas size limits (conservative to ensure compatibility)
    // Chrome/Edge: ~16384x16384, Firefox: ~32767x32767, Safari: ~16384x16384
    // WebView has stricter limits, but only apply them for large PDFs (photos)
    // Regular PDFs (documents) should work fine even in WebView
    const isWebView = typeof window !== 'undefined' && (window.isReactNativeWebView || navigator.userAgent.includes('wv'));
    const isLargePDF = maxPageDimension > 2500 || (pdfPageWidth * pdfPageHeight) > 4000000;

    // Use strict limits only for WebView + Large PDF combination
    const MAX_CANVAS_DIMENSION = (isWebView && isLargePDF) ? 8000 : 16000;
    const MAX_CANVAS_AREA = (isWebView && isLargePDF) ? 33554432 : 268435456; // 32MB for WebView+large, 256MB otherwise

    // Calculate if the extraction will exceed limits
    let adjustedExtractionScale = extractionScale;
    if (finalWidth > MAX_CANVAS_DIMENSION || finalHeight > MAX_CANVAS_DIMENSION || (finalWidth * finalHeight) > MAX_CANVAS_AREA) {
      // Calculate the maximum scale we can use
      const maxScaleForWidth = MAX_CANVAS_DIMENSION / (width / displayScale);
      const maxScaleForHeight = MAX_CANVAS_DIMENSION / (height / displayScale);
      const maxScaleForArea = Math.sqrt(MAX_CANVAS_AREA / ((width / displayScale) * (height / displayScale)));

      adjustedExtractionScale = Math.min(maxScaleForWidth, maxScaleForHeight, maxScaleForArea);

      // Ensure we have at least 2x quality, but don't go below 1x
      adjustedExtractionScale = Math.max(1, Math.min(adjustedExtractionScale, extractionScale));

      console.warn('⚠️ Extraction canvas would exceed browser limits!', {
        pdfType: isLargePDF ? 'Large PDF/Photo' : 'Regular PDF',
        pdfPageDimensions: { width: pdfPageWidth, height: pdfPageHeight },
        requestedSize: { width: finalWidth, height: finalHeight },
        maxDimension: MAX_CANVAS_DIMENSION,
        maxArea: MAX_CANVAS_AREA,
        originalScale: extractionScale,
        adjustedScale: adjustedExtractionScale
      });
    }

    // Get page dimensions using the adjusted extraction scale
    const viewport = page.getViewport({ scale: adjustedExtractionScale, rotation });

    // Create a canvas for rendering the page at high resolution
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    // Validate canvas was created successfully
    if (canvas.width === 0 || canvas.height === 0) {
      console.error('❌ Failed to create canvas - dimensions are zero');
      return null;
    }

    // Render the PDF page to canvas
    const renderContext = {
      canvasContext: context,
      viewport: viewport
    };

    await page.render(renderContext).promise;

    // Validate that something was actually drawn (check center pixel or sample)
    const centerPixel = context.getImageData(canvas.width / 2, canvas.height / 2, 1, 1).data;
    // Check alpha channel (index 3)
    if (centerPixel[3] === 0) {
      // Try checking a few more points to be sure
      const points = [
        { x: canvas.width * 0.25, y: canvas.height * 0.25 },
        { x: canvas.width * 0.75, y: canvas.height * 0.75 },
        { x: canvas.width * 0.5, y: canvas.height * 0.25 }
      ];

      let hasContent = false;
      for (const p of points) {
        const pixel = context.getImageData(p.x, p.y, 1, 1).data;
        if (pixel[3] > 0) {
          hasContent = true;
          break;
        }
      }

      if (!hasContent) {
        console.warn('⚠️ PDF page render resulted in empty/transparent canvas');
        return null; // Trigger fallback
      }
    }

    // Create a new canvas for the extracted area
    const extractedCanvas = document.createElement('canvas');
    const extractedContext = extractedCanvas.getContext('2d');

    // Convert display coordinates to extraction coordinates with adjusted scale
    const adjustedScaleRatio = adjustedExtractionScale / displayScale;
    const scaledX = x * adjustedScaleRatio;
    const scaledY = y * adjustedScaleRatio;
    const scaledWidth = width * adjustedScaleRatio;
    const scaledHeight = height * adjustedScaleRatio;

    // Final validation before creating extraction canvas
    if (scaledWidth <= 0 || scaledHeight <= 0 || scaledWidth > MAX_CANVAS_DIMENSION || scaledHeight > MAX_CANVAS_DIMENSION) {
      console.error('❌ Invalid extraction canvas dimensions:', { scaledWidth, scaledHeight });
      return null;
    }

    console.log('High-resolution extraction:', {
      pdfType: isLargePDF ? 'Large PDF/Photo' : 'Regular PDF',
      pdfPageDimensions: { width: pdfPageWidth, height: pdfPageHeight },
      input: { x, y, width, height },
      displayScale,
      requestedExtractionScale: extractionScale,
      actualExtractionScale: adjustedExtractionScale,
      scaleRatio: adjustedScaleRatio,
      output: { scaledX, scaledY, scaledWidth, scaledHeight },
      extractionCanvasSize: { width: viewport.width, height: viewport.height },
      finalImageSize: { width: scaledWidth, height: scaledHeight },
      qualityMultiplier: `${adjustedExtractionScale}x original PDF resolution`
    });

    extractedCanvas.width = scaledWidth;
    extractedCanvas.height = scaledHeight;

    // Validate extracted canvas
    if (extractedCanvas.width === 0 || extractedCanvas.height === 0) {
      console.error('❌ Failed to create extracted canvas - dimensions are zero');
      return null;
    }

    // Extract the selected area from the full page canvas
    try {
      extractedContext.drawImage(
        canvas,
        scaledX, scaledY, scaledWidth, scaledHeight, // Source rectangle
        0, 0, scaledWidth, scaledHeight // Destination rectangle
      );
    } catch (drawError) {
      console.error('❌ Failed to draw image to extraction canvas:', drawError);
      return null;
    }

    // Convert to PNG with quality setting
    // For WebView, use slightly lower quality to reduce memory usage
    const quality = isWebView ? 0.95 : 1.0;
    let dataURL;

    try {
      dataURL = extractedCanvas.toDataURL('image/png', quality);
    } catch (toDataURLError) {
      console.error('❌ Failed to convert canvas to data URL:', toDataURLError);
      // This often happens when canvas is too large
      return null;
    }

    // Validate the generated image is not empty
    // Empty canvas produces a very short base64 string (less than 100 chars typically)
    if (!dataURL || dataURL.length < 100) {
      console.error('❌ Generated image is empty or invalid');
      return null;
    }

    // Check if the base64 string is unreasonably large (>50MB could crash WebView)
    const imageSizeMB = (dataURL.length * 0.75) / (1024 * 1024); // Approximate size in MB
    if (isWebView && imageSizeMB > 50) {
      console.error('❌ Generated image is too large for WebView:', imageSizeMB.toFixed(2), 'MB');
      return null;
    }

    console.log('✅ Successfully extracted image, size:', (imageSizeMB).toFixed(2), 'MB');

    // Clean up canvases to free memory immediately
    canvas.width = 0;
    canvas.height = 0;
    extractedCanvas.width = 0;
    extractedCanvas.height = 0;

    return dataURL;

  } catch (error) {
    console.error('Error extracting PDF area from page:', error);
    return null;
  }
};